import { Country } from './countries'

/**
 * Supported countries for phone number handling:
 * - United States (+1)
 * - Canada (+1)
 * - Australia (+61)
 * - New Zealand (+64)
 * - Singapore (+65)
 * - South Africa (+27)
 */

export function getCountryCode(country: Country): string {
    switch (country) {
        case 'Australia':
            return '+61'
        case 'New Zealand':
            return '+64'
        case 'Singapore':
            return '+65'
        case 'South Africa':
            return '+27'
        case 'Canada':
        case 'United States':
        default:
            return '+1'
    }
}

export function getCountryCodeFlag(country: Country): string {
    switch (country) {
        case 'Australia':
            return '🇦🇺'
        case 'Canada':
            return '🇨🇦'
        case 'New Zealand':
            return '🇳🇿'
        case 'Singapore':
            return '🇸🇬'
        case 'South Africa':
            return '🇿🇦'
        case 'United States':
        default:
            return '🇺🇸'
    }
}

export function getCountryCodeMask(country: Country): string {
    switch (country) {
        case 'Australia':
            // Australian mobile: 04XX XXX XXX (10 digits with leading 0)
            return '9999 999 999'
        case 'New Zealand':
            // New Zealand mobile: 02X XXX XXXX (9-10 digits with leading 0)
            return '999 999 9999'
        case 'Singapore':
            // Singaporean: XXXX XXXX (8 digits, no trunk prefix)
            return '9999 9999'
        case 'South Africa':
            // South African mobile: 0XX XXX XXXX (10 digits with leading 0)
            return '999 999 9999'
        case 'Canada':
        case 'United States':
        default:
            // North American: (XXX) XXX-XXXX
            return '(999) 999-9999'
    }
}

export function getCountryPhoneLength(country: Country): number {
    switch (country) {
        case 'Australia':
            return 9 // Australian mobile numbers are 9 digits (excluding country code)
        case 'New Zealand':
            return 9 // New Zealand mobile numbers are 8-9 digits (excluding country code)
        case 'Singapore':
            return 8 // Singaporean numbers are 8 digits (excluding country code)
        case 'South Africa':
            return 9 // South African mobile numbers are 9 digits (excluding country code)
        case 'Canada':
        case 'United States':
        default:
            return 10 // North American numbers are 10 digits (excluding country code)
    }
}

export function getCountryPhonePattern(country: Country): RegExp {
    switch (country) {
        case 'Australia':
            // Australian mobile: 4XX XXX XXX, Landline: XX XXXX XXXX
            return /^(\+61\s?)?(0?4\d{2}|[2-9]\d{1})\s?\d{3}\s?\d{3}$/
        case 'New Zealand':
            // New Zealand mobile: 02X XXX XXXX or 021 XXX XXXX
            return /^(\+64\s?)?(0?2[0-9])\s?\d{3}\s?\d{3,4}$/
        case 'Singapore':
            // Singaporean mobile/landline: XXXX XXXX (8 digits starting with 6, 8, or 9)
            return /^(\+65\s?)?[689]\d{3}\s?\d{4}$/
        case 'South Africa':
            // South African mobile: 0XX XXX XXXX
            return /^(\+27\s?)?(0?[6-8][0-9])\s?\d{3}\s?\d{4}$/
        case 'Canada':
        case 'United States':
        default:
            // North American: (XXX) XXX-XXXX
            return /^(\+1\s?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/
    }
}

/**
 * Gets the ISO 3166-1 alpha-2 country code from a country name.
 */
export function getCountryISOCode(country: Country): string {
    switch (country) {
        case 'Australia':
            return 'AU'
        case 'Canada':
            return 'CA'
        case 'New Zealand':
            return 'NZ'
        case 'Singapore':
            return 'SG'
        case 'South Africa':
            return 'ZA'
        case 'United States':
        default:
            return 'US'
    }
}

/**
 * Gets the country name from an E.164 country calling code.
 */
export function getCountryFromCallingCode(callingCode: string): Country {
    switch (callingCode) {
        case '+61':
            return 'Australia'
        case '+64':
            return 'New Zealand'
        case '+65':
            return 'Singapore'
        case '+27':
            return 'South Africa'
        case '+1':
        default:
            return 'United States'
    }
}
